import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'reactstrap';
import { noop } from 'lib/noop';

import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import { validateFieldValue } from 'lib/validations';

/**
 * Component that render input modal for editing sheets
 * @property {boolean} isOpen - parameter that make visible input modal
 * @property {string} activeSheet - parameter that shows which sheet is updating on current moment
 * @property {Function} newSheetsName -function for setting new name to state
 * @property triggerCloseInputModal - function for closing modal
 * @property {Function} renameSheet - function for renaming new Sheet
 * @property {Function} addNewSheet - function for adding new sheet
 * @property {Function} deleteSheet - function for deleting new sheet
 * @property {string} sheetModalType - type of operation that is current at the rendering moment
 * @property {Function} setNewName - function that set new name
 * @property {Array} tabs - array of current sheets names in table component
 * @returns {React.Component} - modalComponent component
 */
const SizeFitConfirmationModal = ({
  isOpen,
  activeSheet,
  newSheetsName,
  triggerCloseInputModal,
  renameSheet,
  addNewSheet,
  deleteSheet,
  sheetModalType,
  setNewName,
  tabs,
  rows
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  let isValid = false;
  const inputValidation = value => {
    const isUniqueValidation = () => {
      return tabs.map(item => item.toLowerCase()).includes(value.toLowerCase())
        ? `Name ${value} is present`
        : undefined;
    };
    const validateFieldMessage = value =>
      validateFieldValue(value, {
        maxLength: 15,
        minLength: 2,
        name: 'New name',
        required: true
      });

    const error = isUniqueValidation() || validateFieldMessage(value) || '';
    isValid = !error;
    setErrorMessage(error);
  };
  const getModalContent = mode => {
    const modalContent = {
      edit: {
        labelText: 'Please enter new name',
        okButtonText: 'Change',
        cancelButtonText: 'Cancel',
        okCallback: () => {
          inputValidation(newSheetsName);
          if (isValid) {
            renameSheet(activeSheet, newSheetsName, rows);
            triggerCloseInputModal();
          }
        }
      },
      add: {
        labelText: 'Please enter sheets name',
        okButtonText: 'Save',
        cancelButtonText: 'Cancel',
        okCallback: () => {
          inputValidation(newSheetsName);
          if (isValid) {
            addNewSheet(newSheetsName);
            triggerCloseInputModal();
          }
        }
      },
      delete: {
        labelText:
          'Are you sure? All your data in the current sheet will be deleted permanently',
        okButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        okCallback: () => {
          deleteSheet(activeSheet);
          triggerCloseInputModal();
        }
      }
    };

    return modalContent[mode];
  };
  const currentModalContent = sheetModalType && getModalContent(sheetModalType);
  return (
    <ConfirmationModal
      isOpen={isOpen}
      yesButtonLabel={currentModalContent.okButtonText}
      noButtonLabel={currentModalContent.cancelButtonText}
      onNoClick={triggerCloseInputModal}
      onYesClick={errorMessage ? noop : currentModalContent.okCallback}
    >
      <p>{currentModalContent.labelText}</p>
      {sheetModalType !== 'delete' && (
        <React.Fragment>
          <Input
            value={newSheetsName}
            invalid={!!errorMessage}
            onChange={event => {
              const { value } = event.currentTarget;
              inputValidation(value);
              setNewName(value);
            }}
          />
          <p>{errorMessage}</p>
        </React.Fragment>
      )}
    </ConfirmationModal>
  );
};

SizeFitConfirmationModal.propTypes = {
  activeSheet: PropTypes.string.isRequired,
  addNewSheet: PropTypes.func.isRequired,
  deleteSheet: PropTypes.func.isRequired,
  renameSheet: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  newSheetsName: PropTypes.string.isRequired,
  sheetModalType: PropTypes.string.isRequired,
  setNewName: PropTypes.func.isRequired,
  triggerCloseInputModal: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default SizeFitConfirmationModal;
